import { immer } from 'zustand/middleware/immer'

import { WaitingListColumn } from '~/app/pages/WaitingList/shared/columns'

import { Slice } from '../store'
import { DrawerActions, DrawerState } from './types'

type SelectedSurgery = {
    id: string
}

type State = {
    columnSortedOn: WaitingListColumn
    sortOrder: 'desc' | 'asc'
    selectedSurgery: SelectedSurgery | null
    selectedPage: number // one-based
    itemsPerPage: number
    pageRawNeedle: string
    showOnlyBookmarked: boolean
    isFetching: boolean
    hasDeniedAccessItems: boolean
    selectedItemByKeyboard: boolean
    keyboardNavigationDisabled: boolean
} & DrawerState

const initialState: State = {
    columnSortedOn: 'WaitingTime',
    sortOrder: 'desc',
    isDrawerOpen: false,
    selectedSurgery: null,
    selectedPage: 1,
    itemsPerPage: 25,
    pageRawNeedle: '',
    showOnlyBookmarked: false,
    isFetching: false,
    hasDeniedAccessItems: false,
    selectedItemByKeyboard: false,
    keyboardNavigationDisabled: false,
}

type Actions = {
    actions: {
        setSelectedSurgery: (surgery: SelectedSurgery) => void
        setColumnSortedOn: (columnSortedOn: State['columnSortedOn']) => void
        setSortOrder: (sortOrder: State['sortOrder']) => void
        toggleSortOrder: () => void
        setSelectedPage: (page: State['selectedPage']) => void
        setPageRawNeedle: (pageRawNeedle: State['pageRawNeedle']) => void
        toggleShowOnlyBookmarked: () => void
        setHasDeniedAccessItems: (value: State['hasDeniedAccessItems']) => void
        setItemsPerPage: (itemsPerPage: State['itemsPerPage']) => void
        setNavigatedByKeyboard: (value: State['selectedItemByKeyboard']) => void
        setDisableKeyboardNavigation: (value: State['keyboardNavigationDisabled']) => void
    } & DrawerActions<never, SelectedSurgery>
}

export type WaitingListSlice = {
    waitingList: State & Actions
}

export const createWaitingListSlice: Slice<WaitingListSlice> = immer((set, get) => ({
    waitingList: {
        ...initialState,
        actions: {
            openDrawer: ({ id }: SelectedSurgery) => {
                const { selectedSurgery, isDrawerOpen } = get().waitingList
                if (isDrawerOpen && selectedSurgery?.id === id) {
                    return
                }

                set(state => {
                    state.waitingList.selectedSurgery = { id }
                    state.waitingList.isDrawerOpen = true
                })
            },
            closeDrawer: () => {
                set(state => {
                    state.waitingList.selectedSurgery = null
                    state.waitingList.isDrawerOpen = false
                })
            },
            setSelectedSurgery: (surgery: SelectedSurgery) => {
                set(state => {
                    state.waitingList.selectedSurgery = surgery
                })
            },
            toggleDrawer: () => {
                set(state => {
                    state.waitingList.isDrawerOpen = !state.waitingList.isDrawerOpen
                })
            },
            setColumnSortedOn(columnSortedOn: State['columnSortedOn']) {
                set(state => {
                    state.waitingList.columnSortedOn = columnSortedOn
                    state.waitingList.sortOrder = initialState.sortOrder
                })
            },
            setSortOrder(sortOrder: State['sortOrder']) {
                set(state => {
                    state.waitingList.sortOrder = sortOrder
                })
            },
            toggleSortOrder: () => {
                set(state => {
                    state.waitingList.sortOrder = state.waitingList.sortOrder === 'asc' ? 'desc' : 'asc'
                })
            },
            setSelectedPage: (page: State['selectedPage']) => {
                set(state => {
                    state.waitingList.selectedPage = page
                })
            },
            setPageRawNeedle: (pageRawNeedle: State['pageRawNeedle']) => {
                set(state => {
                    state.waitingList.pageRawNeedle = pageRawNeedle
                })
            },
            toggleShowOnlyBookmarked: () => {
                set(state => {
                    state.waitingList.showOnlyBookmarked = !state.waitingList.showOnlyBookmarked
                })
            },
            setHasDeniedAccessItems: (value: State['hasDeniedAccessItems']) => {
                set(state => {
                    state.waitingList.hasDeniedAccessItems = value
                })
            },
            setItemsPerPage: (itemsPerPage: State['itemsPerPage']) => {
                set(state => {
                    state.waitingList.itemsPerPage = itemsPerPage
                    state.waitingList.selectedPage = 1
                })
            },
            setNavigatedByKeyboard: (value: State['selectedItemByKeyboard']) => {
                if (get().waitingList.selectedItemByKeyboard === value) {
                    return
                }

                set(state => {
                    state.waitingList.selectedItemByKeyboard = value
                })
            },
            setDisableKeyboardNavigation: (value: State['keyboardNavigationDisabled']) => {
                set(state => {
                    state.waitingList.keyboardNavigationDisabled = value
                })
            },
        },
    },
}))
